import React, { ReactNode, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { StoreState } from '@mm/state/store-interfaces';
import { TRIGGER_DEFINITIONS, TriggerType } from './TriggersDefinitions';
import pluralize from 'pluralize';
import { Link } from '@mm/components';
import { BotBuilderRoutes } from '../../../../../../../routes/bot-builder-routes';
import { activeChatbotIdSelector } from '@mm/state/selectors/viewSelectors';
import { selectRecordsByUiFilterKind } from '@mm/state/selectors/campaignsOverviewSelectors';
import { CampaignsOverviewController } from '@mm/state/controllers/CampaignsOverviewController';
import { CampaignOverviewKindType } from '@mm/state/model/CampaignOverviewModel';

const CampaignName = ({
  id,
  kind = 'automation',
}: {
  id: number;
  kind?: CampaignOverviewKindType;
}) => {
  const botId = useSelector(activeChatbotIdSelector);

  const { campaigns: automations } =
    useSelector((state: StoreState) =>
      selectRecordsByUiFilterKind(state, 'automation'),
    ) || {};

  const getAutomationName = (id: number) => {
    return (
      automations?.find(automation => automation.id === id)?.name ||
      id.toString()
    );
  };

  const [name, setName] = useState('');

  useEffect(() => {
    let nameToSet = getAutomationName(id);

    (async () => {
      if (nameToSet === id.toString()) {
        const res = await CampaignsOverviewController.getAll({
          botId,
          kind,
          ids: [id],
        });
        if (res?.campaigns && res.campaigns[0]) {
          nameToSet = res.campaigns[0].name;
        }
      }

      setName(nameToSet);
    })();
  }, [id]);

  return <>{name}</>;
};

const SKIP_TRIGGERS: TriggerType[] = [
  'instagram_live_comment_guard',
  'instagram_comment_guard',
  'fb_comment_guard',
];

export const useDisabledInputTriggers = ({
  onLinkClick = () => {},
}: {
  onLinkClick?: React.ComponentProps<typeof Link>['onLinkClick'];
}) => {
  const chatbotId = useSelector(activeChatbotIdSelector);

  const externalInputTriggers = useSelector(
    (state: StoreState) => state.automation.existingInputTriggers,
  );

  const usedInExternalAutomations = Object.entries(TRIGGER_DEFINITIONS).reduce(
    (a, [k]) => {
      a[k] = [
        externalInputTriggers?.welcomePage,
        ...(externalInputTriggers?.promoters || []),
      ]
        .filter(Boolean)
        .filter(
          v =>
            v?.kind &&
            v.kind === k &&
            !SKIP_TRIGGERS.includes(v.kind as TriggerType),
        )
        .map(v => v?.activeAutomations?.map(v => v.id))
        .flat()
        .filter(Boolean);

      return a;
    },
    {} as Record<TriggerType, number[]>,
  );

  const template = (
    title: string,
    ids: number[],
    fullText?: string,
    linkName?: string,
  ) => {
    return (
      <span>
        {fullText
          ? fullText
          : `You may only have one ${title} at a time. To create a new one, delete the
          existing ${pluralize('one', ids.length)}: `}
        {ids.slice(0, 1).map((id, i) => (
          <>
            {i !== 0 && ','}
            <Link
              capitalize={false}
              to={BotBuilderRoutes.automationEdit.getLink({ chatbotId, id })}
              onLinkClick={onLinkClick}
            >
              {linkName || <CampaignName id={id} />}
            </Link>
          </>
        ))}
        {ids.length > 1 && '...'}
      </span>
    );
  };

  const templates = Object.entries(TRIGGER_DEFINITIONS).reduce(
    (a, [key, trigger]) => {
      a[key] = usedInExternalAutomations[key].length
        ? template(trigger.name, usedInExternalAutomations[key])
        : undefined;

      return a;
    },
    {} as Record<TriggerType, ReactNode | undefined>,
  );

  return {
    template,
    templates,
  };
};
